import apiUrl from '@/apis/api';
import extractRepoPath from '@/helpers/extractRepoPath';
import { resolveGitlabBaseUrl } from '@/helpers/resolveGitlabBaseUrl';
import { resolveRepositoryProvider } from '@/helpers/resolveRepositoryProvider';

// User type from your auth
interface User {
  UserUUID?: string;
  OrgUUID?: string;
  provider?: string;
  accessToken?: string;
}

export const mountRepoURL = (
  repoName: string,
  provider: string,
  authUrl?: string
) => {
  const resolved = resolveRepositoryProvider(provider);
  if (resolved?.provider === 'github') {
    return `https://github.com/${repoName}`;
  }

  if (resolved?.provider === 'bitbucket') {
    return `https://bitbucket.org/${repoName}`;
  }

  if (resolved?.provider === 'gitlab') {
    return `${resolveGitlabBaseUrl(resolved.baseUrl ?? authUrl)}/${repoName}`;
  }

  throw new Error('Unsupported repository provider');
};

export const repoNameNormalize = (url: string) => {
  return decodeURIComponent(url);
};

export const transformRepoName = (
  url: string
): { repoName: string; vectorDBURL: string; parts: string[] } | null => {
  if (!url) {
    return null;
  }

  const parts = url.split('%26');
  return {
    repoName: parts.join('/'),
    vectorDBURL: parts.join('&'),
    parts,
  };
};

export const handleRepoIngestion = async (
  url: string,
  user: User,
  onSuccess: (msg: string) => void,
  onError: (error: any) => void,
  streamMessages: (url: string, options: any) => Promise<any>,
  authUrl?: string
) => {
  const data = {
    Name: extractRepoPath(url, user?.provider, authUrl),
    Url: url,
    IsPublic: false,
    UserUUID: user?.UserUUID,
    OrgUUID: user?.OrgUUID,
    IngestPrs: true,
    IngestIssues: true,
    IngestDocs: false,
    Branch: 'main',
    AccessToken: user?.accessToken,
  };

  try {
    const response = await streamMessages(`${apiUrl}addRepo2/`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain,/',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'POST,OPTIONS',
      },
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(data),
      timeout: 6000000,
    });
    onSuccess(response);
  } catch (error) {
    onError(error);
  }
};

export const extractRepoName = (
  url: string
): { repoName: string; repoOrg: string } | null => {
  if (!url) return null;

  // Handle github URLs
  if (url.includes('github.com')) {
    const match = url.match(/github\.com\/([^\/]+)\/([^\/]+)/);
    if (match) {
      const [_, org, repo] = match;
      return {
        repoName: repo,
        repoOrg: org,
      };
    }
  }

  return null;
};

export const parseRepoName = (repo: string = '') => {
  const [orgName = '', repoName = ''] = repo.split('/');
  return { orgName, repoName };
};
